/**
 * Shared Supabase data-fetching helpers.
 * Used by db-supabase.ts and db-admission.ts to avoid duplicating
 * the paginated fetchAll implementation.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import { logError } from './logger';

type Tables = Database['almanak']['Tables'];
export type AlmanakTableName = keyof Tables;
export type RowOf<T extends AlmanakTableName> = Tables[T]['Row'];

/**
 * Attempts per page before giving up. Six sleeps of 1, 2, 4, 8, 16 and 30
 * seconds cover roughly a minute of unavailability.
 *
 * Sized from a measurement, not a guess. During a static build on 2026-08-14 an
 * INDEPENDENT curl probe running outside the build — one request every five
 * seconds — also failed, including a 46-second window (07:59:28 to 08:00:14)
 * where the host accepted no connections at all. The previous budget was three
 * attempts with 1s and 2s sleeps: about three seconds, so a blip an order of
 * magnitude shorter than the real one already exhausted it and failed the build.
 *
 * That the outside probe failed too is the important part. A Supavisor pool
 * timeout would only affect clients of that pool while other connections kept
 * working; here the host went away for everyone, which is why the fix is to
 * outlast the gap rather than to widen the pool. Refs: almanak-oqqh.
 */
const DEFAULT_RETRIES = 7;

/** Cap on a single backoff sleep, so the series stays bounded. */
const MAX_BACKOFF_MS = 30_000;

/**
 * Equal-jitter backoff: half the exponential base plus a random half.
 *
 * The jitter is not decoration. The build runs 15 worker processes that fetch
 * the same tables, and when the host drops they fail within milliseconds of one
 * another — six tables inside 8ms in the observed run. Retrying on a bare
 * exponential would send all of them back in lockstep at exactly the same
 * moments, landing every retry as one burst on a host that is still recovering.
 */
function backoffMs(attempt: number): number {
  const base = Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS);
  return base / 2 + Math.random() * (base / 2);
}

/**
 * Run one paged select, retrying transient failures.
 *
 * Both a returned `result.error` and a thrown exception are retried. supabase-js
 * surfaces a dropped connection as `result.error` with a `TypeError: fetch
 * failed` message, but an outright throw is possible too, and previously that
 * path had no retry at all — it went straight up and failed the build.
 *
 * @returns the page's rows
 * @throws the last error if every attempt fails
 */
async function selectPageWithRetry(
  // PromiseLike, not Promise: PostgrestFilterBuilder is a thenable and does not
  // structurally satisfy Promise (no catch/finally/Symbol.toStringTag).
  runSelect: () => PromiseLike<{ data: unknown[] | null; error: { message: string } | null }>,
  table: string,
  retries: number,
): Promise<unknown[]> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const result = await runSelect();
      if (!result.error) return result.data ?? [];
      lastError = new Error(`Failed to fetch ${table}: ${result.error.message}`);
    } catch (err) {
      lastError = new Error(
        `Failed to fetch ${table}: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
    if (attempt < retries - 1) {
      await new Promise(r => setTimeout(r, backoffMs(attempt)));
    }
  }

  throw lastError ?? new Error(`Failed to fetch ${table}`);
}

/**
 * Fetch all rows from a typed almanak table, paginating in chunks of 1000.
 */
export async function fetchAll<T extends AlmanakTableName>(
  sb: SupabaseClient<Database, 'almanak'>,
  table: T,
  retries = DEFAULT_RETRIES,
): Promise<RowOf<T>[]> {
  const PAGE_SIZE = 1000;
  const all: RowOf<T>[] = [];
  let offset = 0;

  while (true) {
    let data: unknown[];
    try {
      const from = offset;
      data = await selectPageWithRetry(
        () => sb.from(table).select('*').range(from, from + PAGE_SIZE - 1),
        table,
        retries,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      logError('db.fetchAll.failed', { table, offset, retries, error: message });
      throw err;
    }

    if (data.length === 0) break;
    all.push(...(data as unknown as RowOf<T>[]));
    if (data.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }

  return all;
}

/**
 * Fetch all rows from a table not in the generated schema.
 * Falls back to untyped Record<string, unknown> rows.
 */
export async function fetchAllUntyped(
  sb: SupabaseClient<Database, 'almanak'>,
  table: string,
  retries = DEFAULT_RETRIES,
): Promise<Record<string, unknown>[]> {
  const PAGE_SIZE = 1000;
  const all: Record<string, unknown>[] = [];
  let offset = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const client = sb as any;
  while (true) {
    let data: unknown[];
    try {
      const from = offset;
      data = await selectPageWithRetry(
        () => client.from(table).select('*').range(from, from + PAGE_SIZE - 1),
        table,
        retries,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      logError('db.fetchAllUntyped.failed', { table, offset, retries, error: message });
      throw err;
    }

    if (data.length === 0) break;
    all.push(...(data as Record<string, unknown>[]));
    if (data.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }
  return all;
}
